import { Mail01, Send03 } from "@velaro/velaro-component-lib";
import { Dataflow04 } from "@velaro/velaro-component-lib";
import { UserPlus01 } from "@velaro/velaro-component-lib";
import { FlipBackward } from "@velaro/velaro-component-lib";
import { Robot } from "@velaro/velaro-component-lib";
import { MessageQuestionSquare } from "@velaro/velaro-component-lib";
import { MessageCheckSquare } from "@velaro/velaro-component-lib";
import { Link02 } from "@velaro/velaro-component-lib";
import { Data } from "@velaro/velaro-component-lib";
import { Play } from "@velaro/velaro-component-lib";
import { Clock } from "@velaro/velaro-component-lib";
import { SvgIcon } from "@/types";
import IconEmail from "@/icons/IconEmail";
import IconHash from "@/icons/IconHash";
import IconLink from "@/icons/IconLink";
import IconList from "@/icons/IconList";
import IconCheckMarkSquare from "@/icons/IconCheckMarkSquare";
import IconPhone from "@/icons/IconPhone";
import IconText from "@/icons/IconText";
import React from "react";
import SendMessageEditor from "./RightAside/ActionEditors/SendMessageEditor";
import ConditionEditor from "./RightAside/ActionEditors/ConditionEditor";
import AskQuestionEditor from "./RightAside/ActionEditors/AskQuestionEditor";
import JumpToEditor from "./RightAside/ActionEditors/JumpToEditor";
import HttpEditor from "./RightAside/ActionEditors/HttpEditor";
import TransferToAIEditor from "./RightAside/ActionEditors/TransferToAIEditor";
import NetsuiteAIEditor from "./RightAside/ActionEditors/NetsuiteAIEditor";
import DynamicsAIEditor from "./RightAside/ActionEditors/DynamicsAIEditor";
import HubSpotAIEditor from "./RightAside/ActionEditors/HubSpotAIEditor";
import BigCommerceAIEditor from "./RightAside/ActionEditors/BigCommerceAIEditor";
import SapAIEditor from "./RightAside/ActionEditors/SapAIEditor";
import TransferToAgentsEditor from "./RightAside/ActionEditors/TransferToAgentsEditor";
import TimeDelayEditor from "./RightAside/ActionEditors/TimeDelayEditor";
import SetDataEditor from "./RightAside/ActionEditors/SetDataEditor";
import { Expression } from "@/components/Expressions/types";
import SendEmailEditor from "./RightAside/ActionEditors/SendEmailEditor";
import AiComposeEmailEditor from "./RightAside/ActionEditors/AiComposeEmailEditor";
import WorkflowSectionEditor from "./RightAside/ActionEditors/WorkflowSectionEditor";
import ClassifyAndRouteEditor from "./RightAside/ActionEditors/ClassifyAndRouteEditor";
import CrmPushEditor from "./RightAside/ActionEditors/CrmPushEditor";
import BookAppointmentEditor from "./RightAside/ActionEditors/BookAppointmentEditor";
import SendCalendlyLinkEditor from "./RightAside/ActionEditors/SendCalendlyLinkEditor";
import RequestPaymentEditor from "./RightAside/ActionEditors/RequestPaymentEditor";
import PostConversationEditor from "./RightAside/ActionEditors/PostConversationEditor";
import SendSmsEditor from "./RightAside/ActionEditors/SendSmsEditor";
import SendRcsEditor from "./RightAside/ActionEditors/SendRcsEditor";
import CallWorkflowEditor from "./RightAside/ActionEditors/CallWorkflowEditor";
import IconCalendar from "@/icons/IconCalendar";
import { IconUploadCloud } from "@velaro/velaro-component-lib";

export const nodeTypeDefs = {
  trigger: "Trigger",
  action: "Action"
};

export type NodeType = keyof typeof nodeTypeDefs;

// ── Trigger definitions ───────────────────────────────────────────────────────
// This is the authoritative list of trigger IDs the workflow engine recognises.
// Add a trigger here AND in the TriggerModal (with correct live/comingSoon flag).
// Never remove an entry — old saved workflows reference these IDs by string.
export const triggerDefs = {
  // ---- Conversation ----
  conversation_opened:        "Conversation Opened",
  conversation_resolved:      "Conversation Resolved",
  conversation_assigned:      "Conversation Assigned",
  conversation_reopened:      "Conversation Reopened",
  label_applied:              "Label Applied",
  bot_handoff_to_agent:       "Bot Handed Off to Agent",
  // ---- Visitor & Contact ----
  incoming_message:           "Incoming Message",
  outgoing_message:           "Outgoing Message",
  visitor_opens_page:         "Visitor Opens a Page",
  visitor_left:               "Visitor Left the Page",
  visitor_returns:            "Visitor Returns to Site",
  form_submitted:             "Form Submitted",
  contact_created:            "Contact Created",
  contact_updated:            "Contact Updated",
  // ---- Agent & Team ----
  unresponsive_agent:         "Unresponsive Agent",
  unresponsive_visitor:       "Unresponsive Visitor",
  agent_timeout:              "Agent Timeout",
  sla_breach:                 "SLA Threshold Reached",
  tier_escalated:             "Routing Tier Escalated",
  // ---- Engagement & Feedback ----
  csat_submitted:             "CSAT Rating Submitted",
  email_opened:               "Campaign Email Opened",
  email_link_clicked:         "Campaign Link Clicked",
  file_uploaded:              "File Uploaded",
  file_downloaded:            "File Downloaded",
  // ---- Time & Schedule ----
  scheduled:                  "Scheduled (Cron)",
  // ---- Integrations (Premium) ----
  appointment_scheduled:      "Appointment Scheduled",
  service_ticket_created:     "Service Ticket Created",
  order_status_changed:       "Order Status Changed",
  deal_stage_changed:         "Deal Stage Changed",
  inbound_email_received:     "Inbound Email Received",
  webhook_received:           "Webhook Received",
};

export type Trigger = keyof typeof triggerDefs;

// Keep for backwards compatibility (old field name in saved data)
// @deprecated — new saves use "visitor_left"
export const LEGACY_TRIGGER_MAP: Record<string, Trigger> = {
  vistior_left: "visitor_left", // typo in original code
};

/**
 * Trigger-specific configuration stored on the trigger node.
 * Each field is optional — only relevant triggers use them.
 *
 * HOW TO ADD CONFIG FOR A NEW TRIGGER:
 *  1. Add a typed field here.
 *  2. Extend TriggerNode below.
 *  3. Add a UI section in TriggerEditor.tsx under the matching trigger case.
 *  4. Ensure velaro-messaging reads the field when evaluating the trigger.
 *
 * AUDIT / DIAGNOSTICS:
 *  Every trigger change is logged via useActivityLog with category "Workflows"
 *  and action "TriggerTypeChanged". Fields: workflowId, oldTrigger, newTrigger.
 *  Every save also creates a WorkflowVersion snapshot (see useWorkflowVersions).
 */
export type TriggerConfig = {
  /** csat_submitted — only fire if rating is within [min, max] (1–5). */
  csatMinRating?: number;
  csatMaxRating?: number;

  /** label_applied — only fire when one of these label IDs is applied. */
  labelIds?: number[];

  /** email_opened / email_link_clicked — scope to a specific broadcast ID or leave empty for any. */
  broadcastId?: number;

  /** sla_breach — minutes of wait before the breach trigger fires. */
  slaThresholdMinutes?: number;

  /** tier_escalated — fire only when routing advances to this tier number. 0 = any tier. */
  tierNumber?: number;

  /** scheduled — standard cron expression (5-part, UTC). e.g. "0 9 * * 1" = Mon 9am UTC. */
  scheduleExpression?: string;
  scheduleTimezone?: string;

  /** deal_stage_changed — HubSpot / Dynamics stage name to match. Empty = any stage. */
  dealStage?: string;

  /** order_status_changed — e.g. "shipped", "delivered", "refunded". Empty = any. */
  orderStatus?: string;

  /**
   * webhook_received — shared secret key. Velaro will include this in the
   * X-Velaro-Webhook-Key header when calling the configured inbound URL,
   * or validate it when an external system POSTs to /api/webhooks/{workflowId}.
   */
  webhookKey?: string;
};

export const actionDefs = {
  sendMessage: {
    name: "Send Message",
    description: "Send an automated message to the conversation as a chatbot",
    icon: Send03,
    editor: SendMessageEditor
  },
  askQuestion: {
    name: "Ask Question",
    description: "Prompt the user with a specific type of question",
    icon: MessageQuestionSquare,
    editor: AskQuestionEditor
  },
  setData: {
    name: "Set Data",
    description:
      "Manually set a custom property on the workflow, conversation, or contact",
    icon: Data,
    editor: SetDataEditor
  },
  condition: {
    name: "Condition",
    description: "Create branching paths in your workflows",
    icon: Dataflow04,
    editor: ConditionEditor
  },
  httpRequest: {
    name: "HTTP Request",
    description: "Uses web requests to perform custom actions",
    icon: Link02,
    editor: HttpEditor
  },
  jumpTo: {
    name: "Jump to",
    description: "Redirects to a specific step within this workflow",
    icon: FlipBackward,
    editor: JumpToEditor
  },
  transferToAgents: {
    name: "Transfer to Agents",
    description:
      "Transfer the conversation from the workflow to agents in the current team or another team",
    icon: UserPlus01,
    editor: TransferToAgentsEditor
  },
  transferToAI: {
    name: "AI Chatbot",
    description: "Transfer the conversation from the workflow to an AI chatbot",
    icon: Robot,
    editor: TransferToAIEditor
  },
  netsuiteAI: {
    name: "NetSuite AI",
    description: "Let an AI assistant handle NetSuite lookups and actions — searches orders, cases, and customer records, asking the visitor for any missing details",
    icon: Robot,
    editor: NetsuiteAIEditor
  },
  dynamicsAI: {
    name: "Dynamics 365 AI",
    description: "Let an AI assistant handle Dynamics 365 CRM — searches contacts and accounts, creates leads, pushes notes, and manages support cases",
    icon: Robot,
    editor: DynamicsAIEditor
  },
  hubSpotAI: {
    name: "HubSpot AI",
    description: "Let an AI assistant handle HubSpot CRM — identify contacts, qualify leads, log conversation summaries, and push notes to contact timelines",
    icon: Robot,
    editor: HubSpotAIEditor
  },
  bigCommerceAI: {
    name: "BigCommerce AI",
    description: "Let an AI assistant handle BigCommerce — look up orders and customers, manage returns, and provide post-purchase support",
    icon: Robot,
    editor: BigCommerceAIEditor
  },
  sapAI: {
    name: "SAP AI",
    description: "Let an AI assistant handle SAP ERP — look up business partners, orders, invoices, inventory, and manage service calls",
    icon: Robot,
    editor: SapAIEditor
  },
  timeDelay: {
    name: "Time Delay",
    description: "Pause workflow execution for a set number of seconds before continuing",
    icon: Clock,
    editor: TimeDelayEditor
  },
  closeConversation: {
    name: "Close Conversation",
    description: "Manage conversation status within your workflow",
    icon: MessageCheckSquare,
    editor: null
  },
  // addNote: {
  //   name: "Add Note",
  //   description: "Add an internal note to contacts or conversations",
  //   icon: NoteIcon
  // },
  // addTags: {
  //   name: "Add Tags",
  //   description: "Add tags to contacts or conversations",
  //   icon: TagIcon
  // },
  // sendSurvey: {
  //   name: "Send Survey",
  //   description: "Send survey",
  //   icon: ConditionIcon // reuses ConditionIcon
  // },
  // collectData: {
  //   name: "Collect Data",
  //   description: "Collect data",
  //   icon: CollectDataIcon
  // },
  // sendTranscript: {
  //   name: "Send Transcript",
  //   description: "Sends a copy of the conversation transcript to email",
  //   icon: SendTranscriptIcon
  // },
  // faq: {
  //   name: "FAQ",
  //   description: "FAQ",
  //   icon: ConditionIcon // reuses ConditionIcon
  // },
  // triggerWorkflow: {
  //   name: "Trigger Another Workflow",
  //   description: "Trigger the start of another workflow from this workflow",
  //   icon: TriggerWorkflowIcon
  // }
  sendEmail: {
    name: "Send Email",
    description: "Send an email to a specified address",
    icon: Mail01,
    editor: SendEmailEditor
  },
  aiComposeEmail: {
    name: "AI Compose Email",
    description: "Describe what you need — AI writes a personalised email from conversation context and sends it automatically",
    icon: Mail01,
    editor: AiComposeEmailEditor
  },
  workflowSection: {
    name: "Section Entry",
    description:
      "Mark a named entry point in this workflow. An AI node can jump here based on detected intent, then continue through this section.",
    icon: FlipBackward,
    editor: WorkflowSectionEditor
  },
  classifyAndRoute: {
    name: "AI Classify & Route",
    description:
      "Classify the visitor's first message with AI and route to the matching team automatically — no questions needed",
    icon: Robot,
    editor: ClassifyAndRouteEditor
  },
  pushToCrm: {
    name: "Push to CRM",
    description:
      "Push a note or full transcript to a CRM contact (HubSpot, Dynamics) at this point in the workflow",
    icon: Data,
    editor: CrmPushEditor
  },
  bookAppointment: {
    name: "Book Appointment",
    description:
      "Book a meeting on Outlook Calendar directly from the workflow — sends a calendar invite to the visitor automatically",
    icon: IconCalendar,
    editor: BookAppointmentEditor
  },
  sendCalendlyLink: {
    name: "Send Calendly Link",
    description:
      "Generate a personalized single-use Calendly scheduling link and send it to the visitor in chat",
    icon: Link02,
    editor: SendCalendlyLinkEditor
  },
  requestPayment: {
    name: "Request Payment",
    description:
      "Send a Stripe payment link in chat — on Apple Messages for Business, renders as a native Apple Pay card so customers pay with Face ID without leaving iMessage",
    icon: Link02,
    editor: RequestPaymentEditor
  },
  postConversation: {
    name: "Post Conversation",
    description:
      "Post conversation transcript to an external endpoint with compliance controls",
    icon: IconUploadCloud,
    editor: PostConversationEditor
  },
  sendSms: {
    name: "Send SMS",
    description: "Send an SMS text message to a phone number",
    icon: Send03,
    editor: SendSmsEditor
  },
  sendRcs: {
    name: "Send RCS",
    description: "Send a rich RCS message (branded sender, rich cards, carousels, suggested replies) with automatic SMS fallback",
    icon: Send03,
    editor: SendRcsEditor
  },
  callWorkflow: {
    name: "Call Workflow",
    description: "Run another workflow inline — execute a reusable sub-workflow at this point, then continue",
    icon: Dataflow04,
    editor: CallWorkflowEditor
  },
  magentoAI: {
    name: "Magento AI",
    description: "Let an AI assistant handle Magento/Adobe Commerce — look up orders, customers, and products in real time",
    icon: Robot,
    editor: null
  },
  squareAI: {
    name: "Square AI",
    description: "Let an AI assistant handle Square — appointments, catalog, orders, and loyalty",
    icon: Robot,
    editor: null
  },
  salesforceAI: {
    name: "Salesforce AI",
    description: "Let an AI assistant handle Salesforce CRM — search contacts, log notes, create leads, and manage cases",
    icon: Robot,
    editor: null
  },
  collectInput: {
    name: "Collect Input",
    description: "Collect a specific piece of information from the visitor before continuing",
    icon: MessageQuestionSquare,
    editor: null
  }
};

export type Action = keyof typeof actionDefs;

export function getNodeIcon(node: WorkflowNode): SvgIcon {
  if (node.type === "action") {
    return actionDefs[node.actionType]?.icon ?? Robot;
  }
  return Play;
}

export const questionDefs = {
  text: { label: "Text", icon: <IconText /> },
  number: { label: "Number", icon: <IconHash /> },
  url: { label: "URL", icon: <IconLink /> },
  phone: { label: "Phone", icon: <IconPhone /> },
  email: { label: "Email Address", icon: <IconEmail /> },
  multipleChoice: { label: "Multiple Choice", icon: <IconList /> },
  multiSelect: { label: "Multi-Select", icon: <IconCheckMarkSquare /> }
  //file: { label: "File Upload", icon: <IconFilePlus /> }
};
export type Question = keyof typeof questionDefs;

export type NodeId = number;

export type CallWorkflowNode = {
  /** ID of the workflow to execute as a sub-workflow at this step. */
  referencedWorkflowId?: number;
};

export type WorkflowNode = {
  id: NodeId;
  type: NodeType;
  name?: string;
  description?: string;
  child?: NodeId;
} & TriggerNode &
  ActionNode &
  SendMessageNode &
  JumpToNode &
  QuestionNode &
  ConditionNode &
  HttpNode &
  TransferToAiNode &
  NetSuiteAINode &
  DynamicsAINode &
  HubSpotAINode &
  BigCommerceAINode &
  TransferToAgentsNode &
  TimeDelayNode &
  SetDataNode &
  EmailNode &
  AiEmailNode &
  WorkflowSectionNode &
  ClassifyAndRouteNode &
  BookAppointmentNode &
  SendCalendlyLinkNode &
  RequestPaymentNode &
  PostConversationNode &
  SmsNode &
  RcsNode &
  CallWorkflowNode;

export type TriggerNode = {
  trigger: Trigger;
  /** When true, no agent is assigned — the workflow handles the conversation as a bot-only flow. */
  overrideRouting: boolean;
  /**
   * Priority for conversation_opened workflow selection. Lower = higher priority (e.g. 10 beats 100).
   * Default 100. Use to ensure a specific-condition workflow wins over a general fallback.
   * Ties are broken by workflow ID (lower ID = older workflow wins).
   */
  routingPriority?: number;
  channels: string[];
  triggerExpression: Expression; // parses to an Expression
  /** Trigger-specific configuration — see TriggerConfig above. */
  triggerConfig?: TriggerConfig;
};

export type ActionNode = {
  actionType: Action;
};

export type SendMessageNode = {
  text: string;
};

export type JumpToNode = {
  jumpToNodeId?: number;
};

export type QuestionNode = {
  questionType: Question;
  question: string;
  options?: NodeOption[]; //for multiple choice questions
  saveAsContactProp: boolean;
  saveAsConversationProp: boolean;
  saveAsWorkflowProp: boolean;
  propName: string;
  invalidReplyAction: "repeat" | "failBranch";
  invalidChild?: NodeId;
  characterLimit?: number;
  minSelections?: number;
  maxSelections?: number;
  optionsSource?: "static" | "dynamic";
  dynamicOptionsVariable?: string;
};

export interface NodeOption {
  childNodeId?: NodeId;
  label?: string;
  value?: string;
}

export interface MultiSelectAnswer {
  selectedValues: string[];
  selectedLabels: string[];
}

export type ConditionNode = {
  conditionOptions?: ConditionNodeOption[]; //for condition nodes
  fallbackChildId?: NodeId;
};

export interface ConditionNodeOption extends NodeOption {
  expression: Expression;
}

export const httpMethods = {
  get: "GET",
  post: "POST",
  put: "PUT",
  delete: "DELETE"
};

export type HttpNode = {
  httpMethod: keyof typeof httpMethods;
  url: string;
  headers: HttpHeader[];
  body: string;
  waitForResponse: boolean;
  /** Optional node to route to when the HTTP request fails. When set, renders an "On error" branch on canvas.
   * null = enabled but not yet connected to a node. undefined = feature off (single-connector layout). */
  errorChildId?: NodeId | null;
};

/**
 * A specialist AI agent this node's AI can delegate conversations to.
 *
 * How it works: The primary AI's prompt is augmented with a delegate_to_{name} tool for each
 * specialist. The `when` field defines the scope/topics this specialist handles — it is injected
 * into the primary AI's system prompt so it knows when to call the delegate tool.
 * The specialist runs with its own AI config + its own MCP servers, then calls return_to_previous
 * when done, restoring control to the primary AI. Nesting is allowed up to OrchestrationDepth (max 5).
 */
export interface ConnectedAiConfig {
  aiConfigId: number;
  name: string;           // Display name, e.g. "Orders Specialist"
  when?: string;          // Scope hint injected into primary AI prompt, e.g. "orders, shipping, returns"
  mcpServerIds?: number[]; // MCP servers available to this specialist (can differ from parent)
  /**
   * Optional instructions injected into the specialist's context when the primary AI delegates here.
   * Use this to give the specialist extra context about WHY it was called, what the customer said,
   * or what it should do first. Think of it as a "handoff note" from the primary AI to the specialist.
   *
   * This is different from the specialist's system prompt (which is always active). These instructions
   * are specific to this delegation path and only injected when this particular handoff occurs.
   *
   * Example: "Before looking anything up, ask the customer to confirm their account email address."
   */
  delegationInstructions?: string;
}

/**
 * A named workflow section this AI can jump to based on detected intent.
 *
 * How it works: The AI's prompt is augmented with a jump_to_{name} tool. When the AI detects
 * the described intent, it calls the tool and workflow execution resumes from the target section
 * node. After the section completes, a Transfer to AI node in the section can return control.
 */
export interface WorkflowJumpTarget {
  nodeId: number;   // ID of the workflowSection node to jump to
  name: string;     // Section name (copied from the target node for reference)
  when?: string;    // Intent description — tell the AI when to jump here
}

export type TransferToAiNode = {
  aiConfigId: number;
  /**
   * Optional persona override for this AI node.
   * Null/absent = use the site's default bot profile.
   * When set, the AI conversation uses this persona's name, greeting, voice, and tone.
   */
  botProfileId?: number;
  /** IDs of MCP server configs to enable for this node (optional — no MCP if empty/absent). */
  mcpServerIds?: number[];
  /** Specialist AI agents this node's AI can delegate to (optional — no orchestration if absent). */
  connectedAiConfigs?: ConnectedAiConfig[];
  /** Named workflow sections this AI can jump to based on detected intent. */
  jumpTargets?: WorkflowJumpTarget[];
  /** Optional team ID to route to when escalating to an agent (overrides default team routing). */
  escalationTeamId?: number;
  /**
   * Optional whitelist of skill tool names this AI node may use.
   * When empty/absent — all site-enabled skills are available (no restriction).
   * When non-empty — only the listed skill names are offered to the AI at runtime.
   */
  permittedSkills?: string[];
};

export type NetSuiteAINode = {
  /** AI Config ID — which LLM and system prompt runs the conversation. */
  aiConfigId?: number;
  /** Optional persona override. Null/absent = use site default bot profile. */
  botProfileId?: number;
  /** NetSuite integration config ID — credentials to connect to the customer's NetSuite instance. */
  netSuiteConfigId?: number;
  /** IDs of MCP server configs to enable for this node (optional — no MCP if empty/absent). */
  mcpServerIds?: number[];
  /** Specialist AI agents this node's AI can delegate to (optional — no orchestration if absent). */
  connectedAiConfigs?: ConnectedAiConfig[];
  /** Named workflow sections this AI can jump to based on detected intent. */
  jumpTargets?: WorkflowJumpTarget[];
};

export type DynamicsAINode = {
  /** AI Config ID — which LLM and system prompt runs the conversation. */
  aiConfigId?: number;
  /** Optional persona override. Null/absent = use site default bot profile. */
  botProfileId?: number;
  /** Dynamics 365 config ID — which D365 org (credentials) to use for CRM operations. */
  dynamicsConfigId?: number;
  /** IDs of MCP server configs to enable for this node (optional — no MCP if empty/absent). */
  mcpServerIds?: number[];
  /** Specialist AI agents this node's AI can delegate to (optional — no orchestration if absent). */
  connectedAiConfigs?: ConnectedAiConfig[];
  /** Named workflow sections this AI can jump to based on detected intent. */
  jumpTargets?: WorkflowJumpTarget[];
};

export type HubSpotAINode = {
  /** AI Config ID — which LLM and system prompt runs the conversation. */
  aiConfigId?: number;
  /** Optional persona override. Null/absent = use site default bot profile. */
  botProfileId?: number;
  /** HubSpot portal config ID — credentials to connect to the customer's HubSpot account. */
  hubSpotConfigId?: number;
  /** IDs of MCP server configs to enable for this node (optional — no MCP if empty/absent). */
  mcpServerIds?: number[];
  /** Specialist AI agents this node's AI can delegate to (optional — no orchestration if absent). */
  connectedAiConfigs?: ConnectedAiConfig[];
  /** Named workflow sections this AI can jump to based on detected intent. */
  jumpTargets?: WorkflowJumpTarget[];
};

export type BigCommerceAINode = {
  /** AI Config ID — which LLM and system prompt runs the conversation. */
  aiConfigId?: number;
  /** Optional persona override. Null/absent = use site default bot profile. */
  botProfileId?: number;
  /** BigCommerce store config ID — credentials to connect to the customer's BigCommerce store. */
  bigCommerceConfigId?: number;
  sapConfigId?: number;
  // pushToCrm node
  crmPushType?: string;
  crmPushConfigId?: number;
  crmPushContactEmail?: string;
  crmPushIncludeTranscript?: boolean;
  crmPushNoteText?: string;
  /** IDs of MCP server configs to enable for this node (optional — no MCP if empty/absent). */
  mcpServerIds?: number[];
  /** Specialist AI agents this node's AI can delegate to (optional — no orchestration if absent). */
  connectedAiConfigs?: ConnectedAiConfig[];
  /** Named workflow sections this AI can jump to based on detected intent. */
  jumpTargets?: WorkflowJumpTarget[];
};

/**
 * A named entry point in the workflow that AI nodes can jump to based on detected intent.
 * Place this node at the start of a logical "section" (e.g. Returns Process, Support Flow).
 * The section runs normally; add a Transfer to AI node at its end to return control to the caller.
 */
export type WorkflowSectionNode = {
  sectionName?: string;
  sectionDescription?: string;
  /**
   * Node ID of the AI node to return control to after this section completes.
   * When set, the editor shows a reminder to end with a Transfer to AI pointing here.
   * Omit for "auto" — meaning the AI that jumped here is implied by the jump_to_ tool call.
   */
  returnToNodeId?: number;
};

/**
 * A single label → team mapping for the AI Classify & Route node.
 */
export interface ClassificationRoute {
  /** The classification label the AI must emit (e.g. "billing", "support"). */
  label: string;
  /** ID of the team to route to when this label is matched. */
  teamId: number;
}

/**
 * AI-powered first-message classification that routes to different teams
 * based on detected visitor intent — no pre-chat survey or bot dialogue needed.
 */
export type ClassifyAndRouteNode = {
  /** System / classification prompt sent to GPT-4o-mini. */
  classificationPrompt?: string;
  /** Label → team mappings. First matching label wins. */
  classificationRoutes?: ClassificationRoute[];
  /** Team to route to if no label matches or the AI call fails. */
  classificationFallbackTeamId?: number;
};

export type TransferToAgentsNode = {
  specificTeam: boolean;
  teamId?: number;
  specificAgent: boolean;
  agentId?: number;
  /** When set, if no agent accepts within this many seconds, the fallback node runs. */
  ringTimeoutSeconds?: number;
  /** Node ID to execute after ring timeout. Requires ringTimeoutSeconds. */
  timeoutFallbackChildId?: number;
};

export type TimeDelayNode = {
  /** Number of seconds to pause before continuing to the next node. */
  delaySeconds?: number;
};

export type SetDataNode = {
  dataToSet: WorkflowPropValue[];
  saveAsContactProp: boolean;
  saveAsConversationProp: boolean;
  saveAsWorkflowProp: boolean;
};

export type EmailNode = {
  emailTo: string;
  emailSubject: string;
  emailBody: string;
  sendTranscript: boolean;
};

export type AiEmailNode = {
  /** Recipient address. Leave blank to use contact's email on file. Supports <<variable>> macros. */
  aiEmailTo?: string;
  /** Optional subject override. If blank, the AI generates a subject from the instructions. */
  aiEmailSubject?: string;
  /** Natural-language instructions describing what the email should say. */
  aiEmailInstructions?: string;
  /** Tone: professional | friendly | formal | urgent. Default: professional. */
  aiEmailTone?: string;
  /** When true, recent conversation messages are included in the AI prompt as context. */
  aiEmailIncludeTranscript?: boolean;
};

export type CrmPushNode = {
  crmPushType: string;          // "hubspot" | "dynamics"
  crmPushConfigId?: number;
  crmPushContactEmail?: string; // supports <<variable>> substitution; defaults to conversation contact
  crmPushIncludeTranscript: boolean;
  crmPushNoteText?: string;     // custom note body when includeTranscript=false
};

/**
 * Book Appointment node — books a calendar event on Outlook Calendar.
 * Execution: WorkflowJobRunner in velaro-messaging calls POST /api/admin/OutlookCalendar/Book.
 * The organizer email and calendar config are read from the site's OutlookCalendarConfig.
 */
export type BookAppointmentNode = {
  /** Workflow variable name containing the visitor's name. Default: "visitorName". */
  bookApptNameVar?: string;
  /** Workflow variable name containing the visitor's email. Default: "visitorEmail". */
  bookApptEmailVar?: string;
  /** Workflow variable name containing the preferred meeting time (ISO or natural language). Default: "preferredTime". */
  bookApptTimeVar?: string;
  /** Workflow variable name containing optional visitor notes for the meeting body. */
  bookApptNotesVar?: string;
  /** Override the default duration (minutes). 0 = use OutlookCalendarConfig default. */
  bookApptDurationOverride?: number;
  /** Override the meeting title. Empty = use TitlePrefix + visitor name. */
  bookApptTitleOverride?: string;
  /** Variable to save the human-readable confirmation message. Default: "bookingConfirmation". */
  bookApptResultVar?: string;
  /** Variable to save the Outlook event ID for later cancellation. Default: "bookingEventId". */
  bookApptEventIdVar?: string;
};

/**
 * Send Calendly Link node — generates a single-use Calendly scheduling link and sends it in chat.
 * Execution: WorkflowJobRunner in velaro-messaging calls POST /api/admin/Calendly/SchedulingLink.
 */
export type SendCalendlyLinkNode = {
  /** Override event type URI. Empty = use CalendlyConfig default event type. */
  calendlyLinkEventTypeUri?: string;
  /** Workflow variable name containing the visitor's email. Default: "visitorEmail". */
  calendlyLinkEmailVar?: string;
  /** Workflow variable name containing the visitor's name. Default: "visitorName". */
  calendlyLinkNameVar?: string;
  /** Variable to save the booking URL for use in subsequent messages. Default: "bookingUrl". */
  calendlyLinkResultVar?: string;
  /** When true, the link is sent in chat immediately after creation. Default: true. */
  calendlyLinkSendInChat?: boolean;
  /** Chat message template. Use <<bookingUrl>> (or whatever resultVar is set to) for the link. */
  calendlyLinkChatMessage?: string;
};

/**
 * Request Payment node — creates a Stripe Checkout session and sends the URL in chat.
 * On Apple Messages for Business, renders as a native Apple Pay card.
 * Execution: WorkflowJobRunner in velaro-messaging calls Stripe API directly.
 * Config (Stripe secret key, webhook secret) set per-site in Integrations → Payments.
 */
export type RequestPaymentNode = {
  /** "fixed" = use paymentAmountFixed; "variable" = read amount from paymentAmountVar. */
  paymentAmountMode?: "fixed" | "variable";
  /** Fixed amount as a decimal string (e.g. "49.99"). */
  paymentAmountFixed?: string;
  /** Workflow variable name containing the amount when amountMode is "variable". */
  paymentAmountVar?: string;
  /** ISO 4217 currency code. Default: "USD". */
  paymentCurrency?: string;
  /** Description shown on the Stripe Checkout page. Supports <<variable>> substitution. */
  paymentDescription?: string;
  /** Message template sent in chat. Use <<paymentUrl>> for the link. */
  paymentMessageText?: string;
  /** Variable to save the payment status (paid / unpaid / expired). Default: "paymentStatus". */
  paymentResultVar?: string;
  /** Variable to save the Stripe Checkout session ID. Default: "paymentSessionId". */
  paymentSessionIdVar?: string;
  /** Override success redirect URL (blank = Stripe account default). */
  paymentSuccessUrl?: string;
  /** Override cancel redirect URL (blank = Stripe account default). */
  paymentCancelUrl?: string;
};

export type PostConversationNode = {
  // Endpoint configuration
  postUrl: string;
  postHeaders: HttpHeader[];
  expectedSuccessCode: number;

  // Content selection
  includeTranscript: boolean;
  includePreChatSurvey: boolean;
  includeIntroBotMessages: boolean;
  includeAgentSurvey: boolean;
  includeContactProperties: boolean;
  includeConversationProperties: boolean;

  // PII Redaction (subscription-gated)
  stripEmails: boolean;
  stripPhoneNumbers: boolean;

  // Compliance Mode (subscription-gated)
  complianceMode: boolean;
  complianceAcknowledged: boolean;

  // Failure handling
  alertOnFailure: boolean;
  failureAlertEmail: string;
};

export type SmsNode = {
  phoneSource: string;
  phoneAttribute?: string;
  smsMessage: string;
  allowedVariables?: Record<string, boolean>;
  confirmRecipient: boolean;
  dailyLimit?: number;
  fallbackAction: string;
  fallbackEmailAttribute?: string;
  enableTranscript: boolean;
};

export type RcsNode = {
  rcsMessageType?: string;
  rcsMessage?: string;
  rcsCards?: Array<{
    title: string;
    description: string;
    imageUrl: string;
    actions: Array<{
      type: string;
      label: string;
      value: string;
    }>;
  }>;
  rcsSuggestedReplies?: string[];
  rcsSmsFallback?: boolean;
  trackDelivery?: boolean;
};

export type HttpHeader = {
  key: string;
  value: string;
};

export interface NodeColors {
  bgColor: string;
  borderColor: string;
  optionColor: string;
}

export interface WorkflowPropValue {
  name: string;
  value: string;
}

export interface Workflow {
  id: number;
  name: string;
  createdBy: string;
  dateCreated: string;
  lastModified: string;
  triggerType: Trigger;
  enabled: boolean;
  nodes: Record<NodeId, WorkflowNode>;
  isTemplate?: boolean;
  templateId?: number | null;
  templateName?: string | null;
  /** User-defined category e.g. "Lead Capture", "Support", "Sales". Null = uncategorized. */
  category?: string | null;
  /** "deprecated" = retired/replaced. Null or absent = active. */
  workflowStatus?: string | null;
  /** ID of the workflow that replaced this one when deprecated. */
  replacedById?: number | null;
  /** Name of the replacement workflow (resolved server-side). */
  replacedByName?: string | null;
}

export type WorkflowListItem = Omit<Workflow, "nodes">;
